import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Loader } from 'components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Order from 'types/entities/Order';

interface ExtractExcelDialogsProps {
  order: Order | null;
  isExtractModalOpen: boolean;
  isReferencesModalOpen: boolean;
  isExtractLoading: boolean;
  specRef: string;
  setSpecRef: (value: string) => void;
  chronoRef: string;
  setChronoRef: (value: string) => void;
  referencesError: string;
  handleExtractModalClose: () => void;
  handleOpenReferencesModal: () => void;
  handleReferencesModalClose: () => void;
  downloadExtractAndRedirectWithReferences: () => void;
  redirectToPreview: (orderIsSubmitted: boolean) => void;
}

const ExtractExcelDialogs: React.FC<ExtractExcelDialogsProps> = ({
  order,
  isExtractModalOpen,
  isReferencesModalOpen,
  isExtractLoading,
  specRef,
  setSpecRef,
  chronoRef,
  setChronoRef,
  referencesError,
  handleExtractModalClose,
  handleOpenReferencesModal,
  handleReferencesModalClose,
  downloadExtractAndRedirectWithReferences,
  redirectToPreview,
}) => {
  const [t] = useTranslation();

  return (
    <>
      <Dialog
        open={isExtractModalOpen}
        onClose={handleExtractModalClose}
        id="excel-export-dialog"
        aria-labelledby="excel-export-dialog-title"
        aria-describedby="excel-export-dialog-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="excel-export-dialog-title">
          <Grid container sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>{t('Excel_Extraction')} </h3>{' '}
            <IconButton onClick={handleExtractModalClose} color="primary">
              <Close />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="excel-export-dialog-description">
            {t('Extract_Excel_Confirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => redirectToPreview(order?.status === 'pending')} variant="text">
            {t('Dont_Extract')}
          </Button>
          {!isExtractLoading && (
            <Button onClick={handleOpenReferencesModal} autoFocus variant="contained">
              {t('Extract')}
            </Button>
          )}
          {!!isExtractLoading && (
            <div className=" buton_container ">
              <Loader size={40} className="loader_container" />
            </div>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={isReferencesModalOpen}
        onClose={handleReferencesModalClose}
        id="references-dialog"
        aria-labelledby="references-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="references-dialog-title" sx={{ pb: 1 }}>
          <Grid container sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>{t('Reference_Details')}</h3>
            <IconButton onClick={handleReferencesModalClose} color="primary">
              <Close />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ minWidth: '120px' }}>
                {t('Spec_Ref')}{' '}
                <Typography color="error" sx={{ display: 'inline' }}>
                  *
                </Typography>
              </Typography>
              <TextField
                id="client_spec_ref"
                autoFocus
                size="small"
                variant="outlined"
                sx={{ width: '200px' }}
                value={specRef}
                onChange={(e) => setSpecRef(e.target.value)}
                error={!!referencesError && !specRef.trim()}
                required
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ minWidth: '120px' }}>
                {t('Chrono_Ref')}{' '}
                <Typography color="error" sx={{ display: 'inline' }}>
                  *
                </Typography>
              </Typography>
              <TextField
                id="mi_gso_chrono_ref"
                size="small"
                variant="outlined"
                sx={{ width: '200px' }}
                value={chronoRef}
                onChange={(e) => setChronoRef(e.target.value)}
                error={!!referencesError && !chronoRef.trim()}
                required
              />
            </Box>
            {referencesError && (
              <Typography color="error" variant="body2">
                {referencesError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', pl: 3, pr: 3, pt: 1 }}>
          <Button onClick={handleReferencesModalClose} variant="text">
            {t('Cancel')}
          </Button>
          <LoadingButton
            loading={!!isExtractLoading}
            onClick={downloadExtractAndRedirectWithReferences}
            variant="contained"
            disabled={!specRef.trim() || !chronoRef.trim()}
          >
            {t('Extract')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExtractExcelDialogs;
